// @flow

import React, { useCallback, useRef, useEffect } from 'react';
import type { Node } from 'react';
import isArray from 'lodash/isArray';
import ClassNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { FormItem, FormInput } from '@riseart/form';
import { Button } from '@riseart/common';

import {
  quizQuestionCls,
  quizQuestionTextCls,
  quizQuestionButtonCls,
  quizQuestionButtonBackCls,
  quizQuestionButtonNextCls,
  quizQuestionOverlayCls,
  quizQuestionActiveCls,
} from './Question.less';

import {
  quizInputQuestionHintCls,
  quizInputQuestionInputWrapperCls,
  quizInputQuestionInputCls,
  quizInputQuestionInputLabeledCls,
  quizInputQuestionLabelCls,
} from './Text.less';

type Props = {
  id: number,
  type: string,
  text: string,
  label: string,
  isActive: boolean,
  isValid: boolean,
  inputType: string,
  hintText: string,
  onChange: Function,
  errors: Array<Object>,
  backButton: {
    translationText: string,
    url: string,
    backListener: Function,
  },
  nextButton: {
    translationText: string,
    url: string,
    nextListener: Function,
  },
  onAnswer: Function,
  error: Object,
};

/**
 * QuizInputQuestion
 *
 * @param {Props} props
 * @returns {Node}
 */
export function QuizInputQuestion({
  id,
  type,
  isActive,
  text,
  hintText,
  label,
  backButton,
  nextButton,
  isValid,
  errors,
  inputType,
  onChange,
}: Props): Node {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const currentIsActive = useRef(isActive);
  const tabIndex = isActive ? '0' : '-1';
  const inputId = `question-field-${id}`;
  const labelComponent = label ? (
    <label htmlFor={inputId} className={quizInputQuestionLabelCls}>
      {label}
    </label>
  ) : null;
  const formItemProps = isArray(errors)
    ? {
        validateStatus: 'error',
        help: errors.map((i) => i.message).join(' ') || (
          <FormattedMessage id="components.quiz.validation_messages.default_message" />
        ),
      }
    : {};

  /**
   * handleChange
   *
   * @param {Event} e
   */
  const handleChange = useCallback(
    (e: SyntheticInputEvent<FormInput>) => {
      const { value } = e.target;

      onChange({ id, type }, inputType === 'int' ? parseInt(value, 10) : value);
    },
    [id, type, inputType, onChange],
  );

  // Called on component mounts
  useEffect(() => {
    if (inputRef.current && isActive) {
      inputRef.current.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus/Blur input depending on isActive flag
  useEffect(() => {
    if (inputRef.current) {
      if (isActive && !currentIsActive.current) {
        inputRef.current.focus();
      } else if (!isActive && currentIsActive.current) {
        inputRef.current.blur();
      }
    }

    currentIsActive.current = isActive;
  }, [isActive]);

  return (
    <div
      className={ClassNames(quizQuestionCls, {
        [quizQuestionActiveCls]: isActive,
      })}
    >
      <div className={quizQuestionOverlayCls} />
      <div>
        <div className={quizQuestionTextCls}>{text}</div>
        <div className={quizInputQuestionHintCls}>{hintText}</div>
      </div>
      <div className={quizInputQuestionInputWrapperCls}>
        <FormItem {...formItemProps}>
          {labelComponent}
          <FormInput
            ref={inputRef}
            tabIndex={tabIndex}
            id={inputId}
            name={`question-input-${id}`}
            className={ClassNames(quizInputQuestionInputCls, {
              [quizInputQuestionInputLabeledCls]: labelComponent,
            })}
            onChange={handleChange}
          />
        </FormItem>
      </div>
      <div className={quizQuestionButtonCls}>
        {backButton ? (
          <Button
            type="text"
            className={quizQuestionButtonBackCls}
            noBorder
            tabIndex={tabIndex}
            disabled={false}
            onClick={backButton.backListener}
          >
            {backButton.translationText}
          </Button>
        ) : null}
        {nextButton ? (
          <Button
            className={quizQuestionButtonNextCls}
            type="black"
            isBlackBorder
            tabIndex={tabIndex}
            disabled={!!isValid}
            onClick={nextButton.nextListener}
          >
            {nextButton.translationText}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
